// src/App.js
import React, { useRef, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";

// --- COMPONENTS ---
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomeSection from "./components/HomeSection";
import AboutSection from "./components/AboutSection";
import TestsSection from "./components/TestsSection";
import LoginPage from "./components/LoginPage";
import SignupPage from "./components/SignupPage";
import ProfilePage from "./components/ProfilePage";
import CategoryListPage from "./components/CategoryListPage";
import AssessmentsPage from "./components/AssessmentsPage";
import QuizPage from "./components/QuizPage";
import VerifyEmailPage from "./components/VerifyEmailPage";
import AdminLogin from "./components/AdminLogin";

// --- TOEFL & IELTS ---
import IeltsListening from "./quiz/IeltsListening";
import IeltsReading from "./quiz/IeltsReading";
import IeltsWriting from "./quiz/IeltsWriting";
import IeltsSpeaking from "./quiz/IeltsSpeaking";
import IeltsMenu from "./quiz/IeltsMenu";
import ListeningTestReady from "./quiz/Ieltscardlistening";
import ReadingTestReady from "./quiz/IeltscardReading";
import SpeakingTestReady from "./quiz/IeltscardSpeaking";
import WritingTestReady from "./quiz/IeltscardWriting";
import ToeflListening from "./quiz/ToeflListening";
import ToeflReading from "./quiz/ToeflReading";
import ToeflWriting from "./quiz/ToeflWriting";
import ToeflSpeaking from "./quiz/ToeflSpeaking";
import ToeflSpeakingAppointment from "./quiz/ToeflSpeakingAppointment";
import ToeflListeningIntro from "./quiz/ToeflListeningIntro";
import ToeflReadingIntro from "./quiz/ToeflcardReading";
import ToeflWritingIntro from "./quiz/ToeflcardWriting";
import ToeflReadingInstructions from "./quiz/ToeflReadingInstructions";
import ToeflWritingInstructions from "./quiz/ToeflWritingInstructions";
import ToeflResultPage from "./quiz/ToeflResultPage";
import AdminDashboard from "./components/AdminDashboard";

// --- PGCET MCA TEST ---
import PgcetmcaMenu from "./quiz/pgcet-mca-test/PgcetmcaMenu";
import PgcetmcaTest from "./quiz/pgcet-mca-test/PgcetmcaTest";
import { PGCET_MCA_MOCK_NUMBERS, pgcetMcaPath } from "./quiz/pgcet-mca-test/pgcetmcaMocks";
// --- PGCET MBA TEST ---
import PgcetmbaMenu from "./quiz/pgcet-mba-test/PgcetmbaMenu";
import PgcetmbaTest from "./quiz/pgcet-mba-test/PgcetmbaTest";
// PGCET MBA Mocks 2–9 each have their own intro and test page.
import Mba2Intro from "./quiz/mba-mock2/Mba2intro";
import Mba2Test from "./quiz/mba-mock2/Mba2Test";
import Mba3Intro from "./quiz/mba-mock3/Mba3intro";
import Mba3Test from "./quiz/mba-mock3/Mba3Test";
import Mba4Intro from "./quiz/mba-mock4/mba4intro";
import Mba4Test from "./quiz/mba-mock4/mba4test";
import Mba5Intro from "./quiz/mba-mock5/mba5intro";
import Mba5Test from "./quiz/mba-mock5/mba5test";
import Mba6Intro from "./quiz/mba-mock6/mba6intro";
import Mba6Test from "./quiz/mba-mock6/mba6test";
import Mba7Intro from "./quiz/mba-mock7/mba7intro";
import Mba7Test from "./quiz/mba-mock7/mba7test";
import Mba8Intro from "./quiz/mba-mock8/mba8intro";
import Mba8Test from "./quiz/mba-mock8/mba8test";
import Mba9Intro from "./quiz/mba-mock9/mba9intro";
import Mba9Test from "./quiz/mba-mock9/mba9test";
// --- GMAT TEST ---
import GmatMenu from "./quiz/gmat-test/GmatMenu";
import GmatTest from "./quiz/gmat-test/GmatTest";
// --- CAT TEST ---
import CatMenu from "./quiz/cat-test/CatMenu";
import CatTest from "./quiz/cat-test/CatTest";
// --- MAT TEST ---
import MatMenu from "./quiz/mat-test/MatMenu";
import MatTest from "./quiz/mat-test/MatTest";

// --- GRE & Other Tests ---
import Gre_verbal from "./quiz/Gre_verbal";
import Gre_quantitative from "./quiz/Gre_quantitative";
import Gre_analytical from "./quiz/Gre_analytical";
import { GRE_MOCKS, greMockPath } from "./quiz/greMocks";

// --- STYLES ---
import "./App.css";
import { isTokenValid, clearExpiredSession } from "./utils/auth";
import { ieltsPath } from "./quiz/ieltsMocks";

const HEADER_OFFSET = 70;

/**
 * isTokenValid() now returns:
 *   true  -> session valid
 *   false -> server says 401/403 (really logged out)
 *   null  -> could not tell (backend down / 5xx). NEVER log the user out on null.
 */

const SessionUnreachable = ({ onRetry }) => (
  <div style={{ padding: "160px 24px 40px", textAlign: "center" }}>
    <p style={{ marginBottom: "12px" }}>
      We can't reach the server right now, so your session couldn't be verified.
    </p>
    <button onClick={onRetry} style={{ padding: "8px 18px", cursor: "pointer" }}>
      Try again
    </button>
  </div>
);

const ProtectedRoute = ({ isLoggedIn, authReady, children }) => {
  // 'checking' | 'ok' | 'denied' | 'unreachable'
  const [status, setStatus] = useState("checking");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Wait until the app-level session check has finished. Without this, a page
    // refresh on a protected URL is redirected to /login (isLoggedIn starts false)
    // before the cookie has been verified.
    if (!authReady) return undefined;

    let active = true;
    setStatus("checking");

    (async () => {
      if (!isLoggedIn) {
        if (active) setStatus("denied");
        return;
      }
      const ok = await isTokenValid("user");
      if (!active) return;
      setStatus(ok === true ? "ok" : ok === false ? "denied" : "unreachable");
    })();

    return () => {
      active = false;
    };
  }, [isLoggedIn, authReady, attempt]);

  if (!authReady || status === "checking") return null; // could render a spinner
  if (status === "denied") return <Navigate to="/login" replace />;
  if (status === "unreachable") {
    return <SessionUnreachable onRetry={() => setAttempt((n) => n + 1)} />;
  }
  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const [status, setStatus] = useState("checking");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setStatus("checking");

    (async () => {
      const ok = await isTokenValid("admin");
      if (!active) return;
      setStatus(ok === true ? "ok" : ok === false ? "denied" : "unreachable");
    })();

    return () => {
      active = false;
    };
  }, [attempt]);

  if (status === "checking") return null;
  if (status === "denied") return <Navigate to="/admin" replace />;
  if (status === "unreachable") {
    return <SessionUnreachable onRetry={() => setAttempt((n) => n + 1)} />;
  }
  return children;
};

// True while the page fills the whole screen: either the Fullscreen API
// (e.g. a test's "Full Screen" button) or the browser's own F11 mode, which
// doesn't fire fullscreenchange, so it is detected from the window size.
const isScreenFull = () =>
  !!document.fullscreenElement ||
  (window.innerHeight >= window.screen.height - 1 &&
    window.innerWidth >= window.screen.width - 1);

const useIsFullscreen = () => {
  const [full, setFull] = useState(isScreenFull);
  useEffect(() => {
    const update = () => setFull(isScreenFull());
    document.addEventListener("fullscreenchange", update);
    window.addEventListener("resize", update);
    return () => {
      document.removeEventListener("fullscreenchange", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return full;
};

function MainContent() {
  const isFullscreen = useIsFullscreen();
  const [theme, setTheme] = useState("dark");

  // Hide the top navbar in full-screen mode. Done with a class rather than
  // unmounting Header, because some test pages hide the .header element
  // themselves and expect it to stay the same element.
  useEffect(() => {
    document.documentElement.classList.toggle("is-fullscreen", isFullscreen);
  }, [isFullscreen]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  // Initial session check (runs once)
  useEffect(() => {
    let active = true;

    (async () => {
      const ok = await isTokenValid("user");
      if (!active) return;

      if (ok === true) {
        setIsLoggedIn(true);
      } else if (ok === false) {
        setIsLoggedIn(false);
        clearExpiredSession("user"); // only on a real 401/403
      }
      // ok === null: server unreachable. Keep the cookie, don't call logout.

      setAuthReady(true);
    })();

    return () => {
      active = false;
    };
  }, []);

  // Periodic re-check while logged in
  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const interval = setInterval(async () => {
      const ok = await isTokenValid("user");
      if (ok === false) {
        await clearExpiredSession("user");
        setIsLoggedIn(false);
      }
      // ok === null: backend restarting or offline. Do nothing, keep the user in.
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [isLoggedIn]);

  useEffect(() => {
    document.body.className = theme === "dark" ? "dark" : "";
  }, [theme]);

  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const testsRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin");

  const scrollToSection = (section) => {
    let ref = null;
    switch (section) {
      case "home":
        ref = homeRef;
        break;
      case "about":
        ref = aboutRef;
        break;
      case "tests":
        ref = testsRef;
        break;
      default:
        break;
    }

    if (ref && ref.current) {
      let top = ref.current.offsetTop - HEADER_OFFSET;

      if (section === "tests") {
        top = ref.current.offsetTop + ref.current.offsetHeight - window.innerHeight;
      }

      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (location.pathname === "/" && location.state?.scrollTarget) {
      scrollToSection(location.state.scrollTarget);
    }
  }, [location]);

  const handleLogin = () => {
    setIsLoggedIn(true);
    setAuthReady(true);
  };

  const handleLogout = async () => {
    setIsLoggedIn(false);
    await clearExpiredSession("user");
    navigate("/");
  };

  const handleExplore = (categoryId) => {
    navigate(`/tests/${categoryId}`);
  };

  const handleAdminLogin = () => {
    navigate("/admin/dashboard");
  };

  // Small helper so every protected route is written once
  const protect = (element) => (
    <ProtectedRoute isLoggedIn={isLoggedIn} authReady={authReady}>
      {element}
    </ProtectedRoute>
  );

  return (
    <>
      {/* Header hidden on all /admin routes so it doesn't overlap the dashboard */}
      {!isAdminRoute && (
        <Header
          theme={theme}
          setTheme={setTheme}
          isLoggedIn={isLoggedIn}
          showLogin={() => navigate("/login")}
          goProfile={() => navigate("/profile")}
        />
      )}
      <Routes>
        {/* HOME PAGE */}
        <Route
          path="/"
          element={
            <>
              <div ref={homeRef}>
                <HomeSection
                  onStartAssessment={() => scrollToSection("tests")}
                  onLearnMore={() => scrollToSection("about")}
                />
              </div>

              <div ref={aboutRef}>
                <AboutSection onStartAssessment={() => scrollToSection("tests")} />
              </div>

              <div ref={testsRef} style={{ minHeight: "100vh", padding: "2rem 0" }}>
                <TestsSection isLoggedIn={isLoggedIn} onExplore={handleExplore} />
              </div>
            </>
          }
        />

        {/* AUTH ROUTES */}
        <Route path="/admin" element={<AdminLogin onLogin={handleAdminLogin} />} />
        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route path="/login" element={<LoginPage onLogin={handleLogin} onSuccess={handleLogin} />} />
        <Route path="/signup" element={<SignupPage onSuccess={handleLogin} />} />
        <Route path="/profile" element={<ProfilePage onLogout={handleLogout} />} />
        <Route path="/verify-email" element={<VerifyEmailPage onLogin={handleLogin} />} />

        {/* GRE TESTS */}
        <Route path="/quiz/GRE/verbal-reasoning" element={protect(<Gre_verbal />)} />
        <Route path="/quiz/GRE/quantitative-analysis" element={protect(<Gre_quantitative />)} />
        <Route path="/quiz/GRE/analytical-writing" element={protect(<Gre_analytical />)} />
        {/* GRE Mock 2+; keyed so switching between mocks never reuses a running test's state */}
        {Object.entries(GRE_MOCKS).flatMap(([n, mock]) => [
          <Route key={`gre${n}-v`} path={greMockPath(n, "verbal")} element={protect(<Gre_verbal key={`gre${n}-v`} {...mock.verbal} />)} />,
          <Route key={`gre${n}-q`} path={greMockPath(n, "quantitative")} element={protect(<Gre_quantitative key={`gre${n}-q`} {...mock.quantitative} />)} />,
          <Route key={`gre${n}-a`} path={greMockPath(n, "analytical")} element={protect(<Gre_analytical key={`gre${n}-a`} {...mock.analytical} />)} />,
        ])}

        {/* IELTS TESTS */}
        <Route path="/quiz/listening/intro" element={protect(<ListeningTestReady />)} />
        <Route path="/quiz/reading/intro" element={protect(<ReadingTestReady />)} />
        <Route path="/quiz/speaking/intro" element={protect(<SpeakingTestReady />)} />
        <Route path="/quiz/writing/intro" element={protect(<WritingTestReady />)} />
        <Route path="/tests/ielts-menu" element={protect(<IeltsMenu />)} />
        <Route path="/quiz/listening" element={protect(<IeltsListening />)} />
        <Route path="/quiz/reading" element={protect(<IeltsReading />)} />
        <Route path="/quiz/writing" element={protect(<IeltsWriting />)} />
        <Route path="/quiz/speaking" element={protect(<IeltsSpeaking />)} />

        {/* IELTS MOCKS 2-9 — same components, question sets in src/quiz/ielts-mock<N> */}
        {[2, 3, 4, 5, 6, 7, 8, 9].flatMap((mock) => [
          <Route key={`${mock}-li`} path={ieltsPath(mock, "listening", "/intro")} element={protect(<ListeningTestReady mock={mock} />)} />,
          <Route key={`${mock}-ri`} path={ieltsPath(mock, "reading", "/intro")} element={protect(<ReadingTestReady mock={mock} />)} />,
          <Route key={`${mock}-si`} path={ieltsPath(mock, "speaking", "/intro")} element={protect(<SpeakingTestReady mock={mock} />)} />,
          <Route key={`${mock}-wi`} path={ieltsPath(mock, "writing", "/intro")} element={protect(<WritingTestReady mock={mock} />)} />,
          <Route key={`${mock}-l`} path={ieltsPath(mock, "listening")} element={protect(<IeltsListening key={mock} mock={mock} />)} />,
          <Route key={`${mock}-r`} path={ieltsPath(mock, "reading")} element={protect(<IeltsReading key={mock} mock={mock} />)} />,
          <Route key={`${mock}-w`} path={ieltsPath(mock, "writing")} element={protect(<IeltsWriting key={mock} mock={mock} />)} />,
          <Route key={`${mock}-s`} path={ieltsPath(mock, "speaking")} element={protect(<IeltsSpeaking key={mock} mock={mock} />)} />,
        ])}


        {/* TOEFL TESTS */}
        <Route path="/quiz/toefl/listening/intro" element={protect(<ToeflListeningIntro />)} />
        <Route path="/quiz/toefl/reading/intro" element={protect(<ToeflReadingIntro />)} />
        <Route path="/quiz/toefl/writing/intro" element={protect(<ToeflWritingIntro />)} />
        <Route path="/quiz/toefl/listening" element={protect(<ToeflListening />)} />
        <Route path="/quiz/toefl/reading" element={protect(<ToeflReading />)} />
        <Route path="/quiz/toefl/writing" element={protect(<ToeflWriting />)} />
        <Route path="/quiz/toefl/speaking" element={protect(<ToeflSpeaking />)} />
        <Route path="/quiz/toefl/speaking/appointment" element={protect(<ToeflSpeakingAppointment />)} />
        <Route path="/quiz/toefl/writing/instruction" element={protect(<ToeflWritingInstructions />)} />
        <Route path="/quiz/toefl/reading/instruction" element={protect(<ToeflReadingInstructions />)} />
        <Route path="/quiz/toefl/result" element={protect(<ToeflResultPage />)} />

        {/* TOEFL MOCKS 2-9 — Reading. Same components, data in src/quiz/toefl-mock<N>/readingData.js */}
        {[2, 3, 4, 5, 6, 7, 8, 9].flatMap((mock) => [
          <Route key={`toefl-${mock}-ri`} path={`/quiz/toefl-mock${mock}/reading/instruction`} element={protect(<ToeflReadingInstructions mock={mock} />)} />,
          <Route key={`toefl-${mock}-rintro`} path={`/quiz/toefl-mock${mock}/reading/intro`} element={protect(<ToeflReadingIntro mock={mock} />)} />,
          <Route key={`toefl-${mock}-r`} path={`/quiz/toefl-mock${mock}/reading`} element={protect(<ToeflReading key={`toefl-mock${mock}`} mock={mock} />)} />,
        ])}

        {/* TOEFL MOCKS 2-9 — Listening. Data in src/quiz/toefl-mock<N>/listeningData.js */}
        {[2, 3, 4, 5, 6, 7, 8, 9].flatMap((mock) => [
          <Route key={`toefl-${mock}-li`} path={`/quiz/toefl-mock${mock}/listening/intro`} element={protect(<ToeflListeningIntro mock={mock} />)} />,
          <Route key={`toefl-${mock}-l`} path={`/quiz/toefl-mock${mock}/listening`} element={protect(<ToeflListening key={`toefl-mock${mock}`} mock={mock} />)} />,
        ])}

        {/* TOEFL MOCK 7 — online Speaking (not linked from the menu; Speaking is booked in-centre). */}
        <Route path="/quiz/toefl-mock7/speaking" element={protect(<ToeflSpeaking key="toefl-mock7" mock={7} />)} />

        {/* TOEFL Speaking is taken in-centre, like IELTS: every mock books an appointment. */}
        {[2, 3, 4, 5, 6, 7, 8, 9].map((mock) => (
          <Route key={`toefl-${mock}-sa`} path={`/quiz/toefl-mock${mock}/speaking/appointment`} element={protect(<ToeflSpeakingAppointment key={`toefl-mock${mock}`} mock={mock} />)} />
        ))}

        {/* TOEFL MOCKS 2-9 — Writing. Data in src/quiz/toefl-mock<N>/writingData.js */}
        {[2, 3, 4, 5, 6, 7, 8, 9].flatMap((mock) => [
          <Route key={`toefl-${mock}-wi`} path={`/quiz/toefl-mock${mock}/writing/instruction`} element={protect(<ToeflWritingInstructions mock={mock} />)} />,
          <Route key={`toefl-${mock}-w`} path={`/quiz/toefl-mock${mock}/writing`} element={protect(<ToeflWriting key={`toefl-mock${mock}`} mock={mock} />)} />,
        ])}

        {/* PGCET MCA TEST */}
        <Route path="/quiz/pgcet" element={protect(<PgcetmcaMenu />)} />
        <Route path="/quiz/pgcet/test" element={protect(<PgcetmcaTest />)} />
        {/* PGCET MCA Mock 2+; keyed so switching mocks never reuses a running test's state */}
        {PGCET_MCA_MOCK_NUMBERS.filter((n) => n > 1).flatMap((n) => [
          <Route key={`pgcet${n}-menu`} path={pgcetMcaPath(n)} element={protect(<PgcetmcaMenu key={`pgcet${n}-menu`} mock={n} />)} />,
          <Route key={`pgcet${n}-test`} path={pgcetMcaPath(n, "test")} element={protect(<PgcetmcaTest key={`pgcet${n}-test`} mock={n} />)} />,
        ])}

        {/* PGCET MBA TEST */}
        <Route path="/quiz/pgcet-mba" element={protect(<PgcetmbaMenu />)} />
        <Route path="/quiz/pgcet-mba/test" element={protect(<PgcetmbaTest />)} />
        <Route path="/quiz/mba-mock2" element={protect(<Mba2Intro />)} />
        <Route path="/quiz/mba-mock2/test" element={protect(<Mba2Test />)} />
        <Route path="/quiz/mba-mock3" element={protect(<Mba3Intro />)} />
        <Route path="/quiz/mba-mock3/test" element={protect(<Mba3Test />)} />
        <Route path="/quiz/mba-mock4" element={protect(<Mba4Intro />)} />
        <Route path="/quiz/mba-mock4/test" element={protect(<Mba4Test />)} />
        <Route path="/quiz/mba-mock5" element={protect(<Mba5Intro />)} />
        <Route path="/quiz/mba-mock5/test" element={protect(<Mba5Test />)} />
        <Route path="/quiz/mba-mock6" element={protect(<Mba6Intro />)} />
        <Route path="/quiz/mba-mock6/test" element={protect(<Mba6Test />)} />
        <Route path="/quiz/mba-mock7" element={protect(<Mba7Intro />)} />
        <Route path="/quiz/mba-mock7/test" element={protect(<Mba7Test />)} />
        <Route path="/quiz/mba-mock8" element={protect(<Mba8Intro />)} />
        <Route path="/quiz/mba-mock8/test" element={protect(<Mba8Test />)} />
        <Route path="/quiz/mba-mock9" element={protect(<Mba9Intro />)} />
        <Route path="/quiz/mba-mock9/test" element={protect(<Mba9Test />)} />

        {/* GMAT TEST */}
        <Route path="/quiz/gmat" element={protect(<GmatMenu />)} />
        <Route path="/quiz/gmat/practice/:sectionKey" element={protect(<GmatTest />)} />
        <Route path="/quiz/gmat/full" element={protect(<GmatTest />)} />

        {/* CAT TEST */}
        <Route path="/quiz/cat" element={protect(<CatMenu />)} />
        <Route path="/quiz/cat/test" element={protect(<CatTest />)} />
        <Route path="/quiz/cat-mock2" element={protect(<CatMenu mock={2} />)} />
        <Route path="/quiz/cat-mock2/test" element={protect(<CatTest mock={2} />)} />
        <Route path="/quiz/cat-mock3" element={protect(<CatMenu mock={3} />)} />
        <Route path="/quiz/cat-mock3/test" element={protect(<CatTest mock={3} />)} />
        <Route path="/quiz/cat-mock4" element={protect(<CatMenu mock={4} />)} />
        <Route path="/quiz/cat-mock4/test" element={protect(<CatTest mock={4} />)} />
        <Route path="/quiz/cat-mock5" element={protect(<CatMenu mock={5} />)} />
        <Route path="/quiz/cat-mock5/test" element={protect(<CatTest mock={5} />)} />

        {/* MAT TEST */}
        <Route path="/quiz/mat" element={protect(<MatMenu />)} />
        <Route path="/quiz/mat/test" element={protect(<MatTest />)} />
      </Routes>
      {!["/login", "/signup"].includes(location.pathname) && !isAdminRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <MainContent />
    </Router>
  );
}

export default App;